import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiHeart, FiGrid, FiList } from 'react-icons/fi';
import api from '../services/api';
import LazyImage from '../components/ui/LazyImage';
import { useWishlist } from '../contexts/WishlistContext';
import { useCart } from '../contexts/CartContext';
import { trackAddToCart } from '../utils/analytics';
import { sortSizes } from '../utils/constants';

const getProductId = (product) => product?.id || product?._id || null;

const getCollectionId = (collection) => collection?.id || collection?._id || null;

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const isProductInCollection = (product, collection) => {
  const collectionId = getCollectionId(collection);
  const collectionSlug = normalizeText(collection?.slug);
  const collectionName = normalizeText(collection?.name);

  const productCollectionId = product?.collection_id || product?.collectionId || product?.collection?.id || product?.collection?._id;
  const productCollectionSlug = normalizeText(product?.collection_slug || product?.collection?.slug);
  const productCollectionName = normalizeText(product?.collection_name || product?.collection?.name);

  if (collectionId && String(productCollectionId) === String(collectionId)) return true;
  if (collectionSlug && productCollectionSlug && collectionSlug === productCollectionSlug) return true;
  if (collectionName && productCollectionName && collectionName === productCollectionName) return true;
  return false;
};

const dedupeProducts = (products) => {
  const seen = new Set();
  const unique = [];

  products.forEach((product) => {
    const id = getProductId(product);
    const fallbackKey = `${product?.name || 'product'}-${product?.price || 0}`;
    const key = id ? `id:${id}` : `fallback:${fallbackKey}`;

    if (!seen.has(key)) {
      seen.add(key);
      unique.push(product);
    }
  });

  return unique;
};

const sortProducts = (products, sortBy) => {
  const sorted = [...products];

  switch (sortBy) {
    case 'oldest':
      sorted.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
      break;
    case 'price_low':
      sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
      break;
    case 'price_high':
      sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
      break;
    case 'name':
      sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      break;
    case 'newest':
    default:
      sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      break;
  }

  return sorted;
};

const CollectionDetail = () => {
  const { slug } = useParams();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { addToCart: addItemToCart } = useCart();
  const [sizePickerProduct, setSizePickerProduct] = useState(null);
  const [addedMessage, setAddedMessage] = useState('');
  const [collection, setCollection] = useState(null);
  const [collectionProducts, setCollectionProducts] = useState([]);
  const [otherProducts, setOtherProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('newest');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });

  useEffect(() => {
    const fetchCollectionData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [collectionResponse, productsResponse] = await Promise.all([
          api.publicAPI.getCollection(slug),
          api.publicAPI.getProducts({ list_only: 'true' })
        ]);

        if (!collectionResponse.data?.success || !collectionResponse.data?.collection) {
          setError('Collection not found');
          return;
        }

        const resolvedCollection = collectionResponse.data.collection;
        const allProducts = productsResponse.data?.success ? (productsResponse.data.products || []) : [];
        const embeddedProducts = resolvedCollection.products || [];

        const matchedFromAll = allProducts.filter((product) => isProductInCollection(product, resolvedCollection));
        const primaryProducts = dedupeProducts([...embeddedProducts, ...matchedFromAll]);

        const primaryIds = new Set(primaryProducts.map((product) => getProductId(product)).filter(Boolean).map(String));
        const nonCollectionProducts = allProducts.filter((product) => {
          const id = getProductId(product);
          if (!id) return true;
          return !primaryIds.has(String(id));
        });

        setCollection(resolvedCollection);
        setCollectionProducts(primaryProducts);
        const dedupedOtherProducts = dedupeProducts(nonCollectionProducts);
        setOtherProducts(dedupedOtherProducts);
      } catch (err) {
        setError(api.utils.handleError(err));
      } finally {
        setLoading(false);
      }
    };

    if (slug) fetchCollectionData();
  }, [slug]);

  const suggestedProducts = useMemo(
    () => sortProducts(otherProducts, sortBy),
    [otherProducts, sortBy]
  );

  const filterByPrice = (list) => {
    const minPrice = priceRange.min ? parseFloat(priceRange.min) : null;
    const maxPrice = priceRange.max ? parseFloat(priceRange.max) : null;

    return list.filter((product) => {
      const productPrice = Number(product.price) || 0;
      if (minPrice !== null && productPrice < minPrice) return false;
      if (maxPrice !== null && productPrice > maxPrice) return false;
      return true;
    });
  };

  const filteredCollectionProducts = useMemo(
    () => filterByPrice(sortProducts(collectionProducts, sortBy)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collectionProducts, sortBy, priceRange]
  );

  const filteredSuggestedProducts = useMemo(
    () => filterByPrice(suggestedProducts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [suggestedProducts, priceRange]
  );

  const combinedProducts = useMemo(
    () => [...filteredCollectionProducts, ...filteredSuggestedProducts],
    [filteredCollectionProducts, filteredSuggestedProducts]
  );

  const addToCart = (event, product) => {
    event.preventDefault();
    event.stopPropagation();

    const availableSizes = sortSizes(api.utils.getAvailableSizes(product));
    if (availableSizes.length === 0) return;

    if (availableSizes.length === 1) {
      confirmAddToCart(product, availableSizes[0]);
      return;
    }

    setSizePickerProduct(product);
  };

  const confirmAddToCart = (product, size) => {
    addItemToCart(product, size, 1);
    trackAddToCart(product, 1, size);
    setSizePickerProduct(null);
    setAddedMessage(`${product.name} (${size}) added to cart`);
    window.setTimeout(() => setAddedMessage(''), 2000);
  };

  const addToWishlist = async (event, product) => {
    event.preventDefault();
    event.stopPropagation();
    await toggleWishlist(getProductId(product));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Error</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <Link to="/" className="btn-primary">
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Collection Not Found</h2>
          <p className="text-gray-600 mb-8">The collection you're looking for doesn't exist.</p>
          <Link to="/" className="btn-primary">
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  const renderProductCard = (product, index) => (
    <Link
      key={getProductId(product) || `product-${index}`}
      to={getProductId(product) ? `/products/${getProductId(product)}` : '/products'}
      className={`group relative ${
        viewMode === 'list'
          ? 'flex items-center space-x-4 p-4 border rounded-lg hover:shadow-lg transition-shadow'
          : 'border rounded-lg overflow-hidden hover:shadow-lg transition-shadow'
      }`}
    >
      {viewMode === 'grid' ? (
        <>
          {/* Product Image */}
          <div className="relative aspect-[4/5] overflow-hidden">
            <LazyImage
              src={product.images?.[0]}
              alt={product.name}
              width={600}
              height={750}
              fit="cover"
              className="group-hover:scale-105 transition-transform duration-300"
            />

            {/* Quick Actions */}
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={(event) => addToWishlist(event, product)}
                className={`p-2 bg-white rounded-full shadow-lg transition-colors ${
                  isInWishlist(getProductId(product))
                    ? 'text-red-600'
                    : 'text-gray-600 hover:bg-red-50 hover:text-red-600'
                }`}
                title="Add to wishlist"
              >
                <FiHeart
                  size={16}
                  fill={isInWishlist(getProductId(product)) ? 'currentColor' : 'none'}
                />
              </button>
            </div>

            {/* Stock Badge */}
            {product.totalStock === 0 && (
              <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                Out of Stock
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="product-card-info">
            <h3 className="text-base lg:text-sm font-medium mb-2 line-clamp-2 group-hover:text-black transition-colors">
              {product.name}
            </h3>

            <div className="flex items-center justify-between mb-2">
              <span className="text-lg lg:text-base font-bold">₹{product.price?.toLocaleString()}</span>
              {product.original_price && product.original_price > product.price && (
                <span className="text-sm text-gray-500 line-through">
                  ₹{product.original_price.toLocaleString()}
                </span>
              )}
            </div>

            <div className="text-sm text-gray-600 mb-3">
              {product.collection_name && (
                <p className="mb-1">{product.collection_name}</p>
              )}
              {product.totalStock > 0 ? (
                <p className="text-green-600">In Stock</p>
              ) : (
                <p className="text-red-600">Out of Stock</p>
              )}
            </div>

            <button
              type="button"
              onClick={(event) => addToCart(event, product)}
              disabled={product.totalStock === 0}
              className={`w-full py-2 px-4 rounded font-medium transition-colors ${
                product.totalStock === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-black text-white hover:bg-gray-800'
              }`}
            >
              {product.totalStock === 0 ? 'Out of Stock' : 'Add to Cart'}
            </button>
          </div>
        </>
      ) : (
        <>
          {/* List View */}
          <div className="relative w-24 aspect-[4/5] flex-shrink-0 overflow-hidden rounded">
            <LazyImage
              src={product.images?.[0]}
              alt={product.name}
              width={600}
              height={750}
              fit="cover"
            />
          </div>

          <div className="flex-1">
            <h3 className="font-medium mb-1">{product.name}</h3>
            <p className="text-sm text-gray-600 mb-2">{product.collection_name}</p>
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold">₹{product.price?.toLocaleString()}</span>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={(event) => addToWishlist(event, product)}
                  className={`p-2 transition-colors ${
                    isInWishlist(getProductId(product))
                      ? 'text-red-600'
                      : 'text-gray-600 hover:text-red-600'
                  }`}
                >
                  <FiHeart
                    size={16}
                    fill={isInWishlist(getProductId(product)) ? 'currentColor' : 'none'}
                  />
                </button>
                <button
                  type="button"
                  onClick={(event) => addToCart(event, product)}
                  disabled={product.totalStock === 0}
                  className={`px-4 py-2 rounded font-medium transition-colors ${
                    product.totalStock === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-black text-white hover:bg-gray-800'
                  }`}
                >
                  {product.totalStock === 0 ? 'Out of Stock' : 'Add to Cart'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </Link>
  );

  const gridClass = viewMode === 'grid' ? 'product-grid' : 'space-y-6';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Collection Header */}
      <div className="mb-8">
        <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
          <Link to="/" className="hover:text-black">Home</Link>
          <span>/</span>
          <Link to="/collections" className="hover:text-black">Collections</Link>
          <span>/</span>
          <span className="text-black">{collection.name}</span>
        </nav>
        
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">{collection.name}</h1>
          {collection.description && (
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
              {collection.description}
            </p>
          )}
          <p className="text-gray-500">
            {combinedProducts.length} products
          </p>
        </div>
      </div>

      {/* Filters and Sort */}
      <div className="mb-8 border-b border-gray-200 pb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* View Mode Toggle */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">View:</span>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-black text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              <FiGrid size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-black text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              <FiList size={16} />
            </button>
          </div>

          {/* Sort Options */}
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
              <option value="name">Name: A to Z</option>
            </select>
          </div>

          {/* Price Filter */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Price:</span>
            <input
              type="number"
              placeholder="Min"
              value={priceRange.min}
              onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
              className="w-20 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-black"
            />
            <span className="text-gray-400">-</span>
            <input
              type="number"
              placeholder="Max"
              value={priceRange.max}
              onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
              className="w-20 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>
        </div>
      </div>

      {/* Products Grid/List */}
      {combinedProducts.length > 0 ? (
        <>
          {filteredCollectionProducts.length > 0 && (
            <div className={gridClass}>
              {filteredCollectionProducts.map((product, index) => renderProductCard(product, index))}
            </div>
          )}

          {filteredSuggestedProducts.length > 0 && (
            <div className="mt-16">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold">You May Also Like</h2>
                <p className="text-gray-500 mt-2">Handpicked styles from across our store</p>
              </div>
              <div className={gridClass}>
                {filteredSuggestedProducts.map((product, index) => renderProductCard(product, `suggested-${index}`))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <h3 className="text-xl font-medium mb-2">No products found</h3>
          <p className="text-gray-600 mb-6">This collection doesn't have any products yet.</p>
          <Link to="/" className="btn-primary">
            Browse All Products
          </Link>
        </div>
      )}

      {/* Size Picker Modal */}
      {sizePickerProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
          onClick={() => setSizePickerProduct(null)}
        >
          <div
            className="bg-white rounded-lg p-6 w-full max-w-sm"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-1">Select Size</h3>
            <p className="text-sm text-gray-600 mb-4 line-clamp-1">{sizePickerProduct.name}</p>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {sortSizes(api.utils.getAvailableSizes(sizePickerProduct)).map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => confirmAddToCart(sizePickerProduct, size)}
                  className="border border-gray-300 rounded py-2 text-sm font-medium hover:bg-black hover:text-white transition-colors"
                >
                  {size}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setSizePickerProduct(null)}
              className="w-full py-2 text-sm text-gray-600 hover:text-black transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Added-to-cart toast */}
      {addedMessage && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-black text-white text-sm px-4 py-2 rounded shadow-lg">
          {addedMessage}
        </div>
      )}
    </div>
  );
};

export default CollectionDetail;
